import {
	FeedbackReportKind,
	FeedbackReportPriority,
	FeedbackReportStatus,
} from '@run-diffusion/shared';
import { forgetWebClientDeviceIfRejected, internalApiCredentials } from '@/services/internalApiCredentials';
import { FEEDBACK_API_BASE_URL, feedbackApiFailure, feedbackApiRequest, FeedbackApiRequest, FeedbackApiResult } from '@/utils/feedbackApi';
import { ReportHolder } from './feedbackReports';

// Every change the Feedback page makes to a report, through restServer's feedback service as the
// signed-in person (utils/feedbackApi.ts, ADR 0042). Each resolves to the API's answer; the page's
// Firestore listeners see the change itself. A refusal carries the API's code and its words, such
// as FEEDBACK_REPORT_MOVED when the report changed since the page showed it.

export type { FeedbackApiFailure, FeedbackApiResult } from '@/utils/feedbackApi';
export { feedbackApiFailure } from '@/utils/feedbackApi';

// What the API answers for a report (feedback_service.get_report): the page only reads its number
// and status back, and an added comment's id.
export interface FeedbackApiReport {
	number: number
	status: FeedbackReportStatus
	created?: boolean
}

// The claim a person takes over, as the page showed it: whose, and when it was made (feedback_service.claim_ref).
export interface TakeoverOf {
	uid: string
	claimed_at: number | null
}

export const takeoverOf = (holder: ReportHolder | null): TakeoverOf | null =>
	(holder ? { uid: holder.uid, claimed_at: holder.since ? holder.since.getTime() : null } : null);

const send = async <T>(request: Omit<FeedbackApiRequest, 'baseUrl' | 'idToken' | 'deviceId'>): Promise<FeedbackApiResult<T>> => {
	const credentials = await internalApiCredentials();
	if (!credentials) {
		return { ok: false, status: 401, code: 'NOT_SIGNED_IN', message: 'You\'re signed out. Sign in again, then try that again.', details: null };
	}
	const result: FeedbackApiResult<T> = await feedbackApiRequest<T>({ baseUrl: FEEDBACK_API_BASE_URL, ...credentials, ...request });
	const failure = feedbackApiFailure(result);
	if (failure) forgetWebClientDeviceIfRejected(failure.code);
	return result;
};

const reportPath = (number: number, what: string): string => `reports/${Number(number)}/${what}`;

export interface MoveOptions {
	note?: string
	// the status the page showed: a report that moved since is refused
	expected: FeedbackReportStatus
	takeoverOf?: TakeoverOf | null
	duplicateOf?: number | null
	pullRequest?: string | null
}

export const moveReport = (number: number, status: FeedbackReportStatus, options: MoveOptions) => send<FeedbackApiReport>({
	method: 'POST',
	path: reportPath(number, 'status'),
	body: {
		status,
		note: options.note || '',
		expected: options.expected,
		...(options.takeoverOf ? { takeover_of: options.takeoverOf } : {}),
		...(options.duplicateOf ? { duplicate_of: options.duplicateOf } : {}),
		...(options.pullRequest ? { pull_request: options.pullRequest } : {}),
	},
});

// Claim it for yourself, take it over (naming the claim), or assign it to another system admin.
export const claimReport = (number: number, options: { expected: FeedbackReportStatus, takeoverOf?: TakeoverOf | null, assignee?: string | null }) => send<FeedbackApiReport>({
	method: 'POST',
	path: reportPath(number, 'claim'),
	body: {
		expected: options.expected,
		...(options.takeoverOf ? { takeover_of: options.takeoverOf } : {}),
		...(options.assignee ? { assignee: options.assignee } : {}),
	},
});

export const releaseReport = (number: number, expected: FeedbackReportStatus) => send<FeedbackApiReport>({
	method: 'POST', path: reportPath(number, 'release'), body: { expected },
});

export const setPriority = (number: number, priority: FeedbackReportPriority | null) => send<FeedbackApiReport>({
	method: 'POST', path: reportPath(number, 'priority'), body: { priority },
});

export const setKind = (number: number, kind: FeedbackReportKind) => send<FeedbackApiReport>({
	method: 'POST', path: reportPath(number, 'kind'), body: { kind },
});

export const setTags = (number: number, change: { add?: string[], remove?: string[] }) => send<FeedbackApiReport>({
	method: 'POST', path: reportPath(number, 'tags'), body: { add: change.add || [], remove: change.remove || [] },
});

export const setPullRequest = (number: number, url: string) => send<FeedbackApiReport>({
	method: 'POST', path: reportPath(number, 'pull-request'), body: { url },
});

export const markMerged = (number: number, note: string = '') => send<FeedbackApiReport>({
	method: 'POST', path: reportPath(number, 'merged'), body: { note },
});

export const addComment = (number: number, body: string, options: { replyTo?: string | null, shots?: File[] } = {}) => send<{ id: string }>({
	method: 'POST',
	path: reportPath(number, 'comments'),
	body: { body, ...(options.replyTo ? { reply_to: options.replyTo } : {}) },
	files: options.shots || [],
	part: 'comment',
});

export const editComment = (number: number, commentId: string, body: string) => send<FeedbackApiReport>({
	method: 'PATCH', path: reportPath(number, `comments/${encodeURIComponent(commentId)}`), body: { body },
});

export const deleteComment = (number: number, commentId: string) => send<FeedbackApiReport>({
	method: 'DELETE', path: reportPath(number, `comments/${encodeURIComponent(commentId)}`),
});

// A report a person files on the page (feedback_service.create_report).
export interface NewReportFields {
	kind: FeedbackReportKind
	happened: string
	expected: string
	status: FeedbackReportStatus
	note: string
	priority: FeedbackReportPriority | null
	tags: string[]
	link: string
}

export const createReport = (fields: NewReportFields, shots: File[] = []) => send<FeedbackApiReport>({
	method: 'POST',
	path: 'reports',
	body: {
		kind: fields.kind,
		happened: fields.happened,
		expected: fields.expected,
		status: fields.status,
		...(fields.note ? { note: fields.note } : {}),
		...(fields.priority ? { priority: fields.priority } : {}),
		...(fields.tags.length ? { tags: fields.tags } : {}),
		...(fields.link ? { link: fields.link } : {}),
	},
	files: shots,
	part: 'report',
});
